import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import RichEditor from './RichEditor';
import PageNodeRow, { flattenTree } from './PageNode';
import { useConfirm } from './ConfirmDialog';
import { useImagePaste } from '../hooks/useImagePaste';
import {
  Plus, FileText, Trash, CaretRight, CaretDown, DotsThree,
  Lock, ClockCounterClockwise, BookOpen, ArrowsClockwise,
} from '@phosphor-icons/react';
import { PAGE_TEMPLATES } from '../lib/pageTemplates';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';
import { toast } from 'sonner';

// Build a tree from a flat page list keyed by parent_page_id (null = root).
function buildTree(pages) {
  const byParent = {};
  for (const p of pages) {
    const k = p.parent_page_id || '__root__';
    (byParent[k] = byParent[k] || []).push(p);
  }
  const sortFn = (a, b) => (a.position ?? 0) - (b.position ?? 0) || (a.created_at < b.created_at ? -1 : 1);
  Object.values(byParent).forEach(arr => arr.sort(sortFn));
  const attach = (node) => ({ ...node, children: (byParent[node.page_id] || []).map(attach) });
  return (byParent['__root__'] || []).map(attach);
}

// Recursive tree row.
// (PageNode lives in its own file — see ./PageNode.js)

function fmtTimeAgo(ts) {
  if (!ts) return '';
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Main view — owns page tree state, the editor state, and autosave.
export default function PagesView({ projectId, project, members, canEdit }) {
  const { getOrgHeaders, API, user } = useAuth();
  const { uploadImage } = useImagePaste(getOrgHeaders);
  const confirm = useConfirm();
  const [pages, setPages] = useState([]);
  const [limit, setLimit] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [activePage, setActivePage] = useState(null); // full page incl. content
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [savingState, setSavingState] = useState('idle'); // idle | saving | saved | error
  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef({ title: '', content: '' });

  const headers = useCallback(() => ({ withCredentials: true, headers: getOrgHeaders() }), [getOrgHeaders]);

  // Load list of pages (no content) for the tree.
  const fetchPages = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/pages`, headers());
      setPages(data.pages || []);
      setLimit(data.limit ?? null);
      setRemaining(data.remaining ?? null);
      // Auto-pick the first page if none active.
      if ((data.pages || []).length > 0 && !activeId) setActiveId(data.pages[0].page_id);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [API, projectId, headers, activeId]);

  useEffect(() => { fetchPages(); /* eslint-disable-next-line */ }, [projectId]);

  // Load the active page's full content.
  useEffect(() => {
    let cancelled = false;
    if (!activeId) { setActivePage(null); setDraftTitle(''); setDraftContent(''); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pages/${activeId}`, headers());
        if (cancelled) return;
        setActivePage(data);
        setDraftTitle(data.title || '');
        setDraftContent(data.content || '');
        lastSavedRef.current = { title: data.title || '', content: data.content || '' };
        setSavingState('idle');
      } catch (e) { console.error(e); }
    })();
    return () => { cancelled = true; };
  }, [activeId, API, headers]);

  // Debounced autosave — fires 1.2s after the last edit.
  const autoSave = useCallback(() => {
    if (!activeId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSavingState('saving');
    saveTimerRef.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/pages/${activeId}`, {
          title: draftTitle, content: draftContent,
        }, headers());
        lastSavedRef.current = { title: draftTitle, content: draftContent };
        setSavingState('saved');
        // Refresh list (so updated_at + title in tree is current).
        fetchPages();
      } catch (err) {
        setSavingState('error');
        toast.error(err.response?.data?.detail || 'Failed to save');
      }
    }, 1200);
  }, [activeId, draftTitle, draftContent, API, headers, fetchPages]);

  // Trigger autosave whenever draft changes.
  useEffect(() => {
    if (!activePage) return;
    if (draftTitle === lastSavedRef.current.title && draftContent === lastSavedRef.current.content) return;
    autoSave();
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [draftTitle, draftContent, activePage, autoSave]);

  const tree = useMemo(() => buildTree(pages), [pages]);

  const toggle = (id) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Create a new page from a template (or blank).
  const handleCreate = async (templateId, parentId = null) => {
    if (!canEdit) return;
    try {
      const tpl = PAGE_TEMPLATES.find(t => t.id === templateId) || PAGE_TEMPLATES[0];
      const built = tpl.build();
      const { data } = await axios.post(`${API}/projects/${projectId}/pages`, {
        ...built, parent_page_id: parentId, template: templateId,
      }, headers());
      setPages(prev => [...prev, data]);
      setActiveId(data.page_id);
      if (parentId) setExpanded(prev => new Set(prev).add(parentId));
      toast.success('Page created');
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error(err.response.data.detail || 'Free plan limit reached');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to create page');
      }
    }
  };

  const handleDelete = async (page) => {
    if (!(await confirm({
      title: `Delete "${page.title}"?`,
      description: 'This will also delete any subpages. This action cannot be undone.',
      confirmText: 'Delete', variant: 'destructive',
    }))) return;
    try {
      await axios.delete(`${API}/pages/${page.page_id}`, headers());
      setPages(prev => prev.filter(p => p.page_id !== page.page_id && p.parent_page_id !== page.page_id));
      if (activeId === page.page_id) setActiveId(null);
      toast.success('Page deleted');
      fetchPages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const memberMentions = useMemo(() => (members || []).filter(m => m && m.user_id && m.name), [members]);
  const limitReached = remaining !== null && remaining <= 0;

  return (
    <div className="h-full flex" data-testid="pages-view">
      {/* Left rail — page tree */}
      <aside className="w-64 flex-shrink-0 border-r border-zinc-200 bg-zinc-50/40 overflow-y-auto flex flex-col">
        <div className="px-3 py-2.5 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <BookOpen size={13} className="text-zinc-500" />
            <h3 className="text-[11px] font-bold text-zinc-700 uppercase tracking-wide">Docs</h3>
          </div>
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="page-create-root-btn"
                  className="w-6 h-6 flex items-center justify-center rounded hover:bg-white border border-zinc-200 text-zinc-600 hover:text-blue-600">
                  <Plus size={12} weight="bold" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wide">Choose a template</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {PAGE_TEMPLATES.map(t => (
                  <DropdownMenuItem key={t.id} onClick={() => handleCreate(t.id)} disabled={limitReached}
                    data-testid={`page-template-${t.id}`} className="flex-col items-start gap-0.5 py-2">
                    <div className="flex items-center gap-2 w-full">
                      <span className="text-[15px]">{t.icon}</span>
                      <span className="text-[12px] font-semibold text-zinc-800 flex-1">{t.title}</span>
                    </div>
                    <span className="text-[10px] text-zinc-500 ml-7">{t.description}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {limit !== null && limit > 0 && (
          <div className="px-3 py-2 bg-amber-50/70 border-b border-amber-100 text-[10px] text-amber-800 flex items-center gap-1.5" data-testid="pages-quota-banner">
            <Lock size={11} weight="bold" />
            <span><strong className="tabular-nums">{remaining}</strong> of {limit} pages remaining</span>
          </div>
        )}
        <div className="flex-1 py-2 overflow-y-auto">
          {pages.length === 0 && !loading && (
            <div className="text-center px-4 py-8">
              <FileText size={24} className="mx-auto text-zinc-300 mb-2" />
              <p className="text-[12px] text-zinc-500 mb-3">No docs yet</p>
              {canEdit && (
                <Button size="sm" onClick={() => handleCreate('blank')} className="h-7 text-[11px]" data-testid="page-empty-create-btn">
                  <Plus size={12} className="mr-1" /> Create first page
                </Button>
              )}
            </div>
          )}
          {flattenTree(tree, expanded).map(({ node, depth, hasChildren }) => (
            <PageNodeRow key={node.page_id} node={node} depth={depth} hasChildren={hasChildren}
              expanded={expanded} activeId={activeId} onSelect={setActiveId}
              onCreateChild={(pid) => handleCreate('blank', pid)}
              onDelete={handleDelete} onToggle={toggle} canEdit={canEdit} />
          ))}
        </div>
      </aside>

      {/* Main editor */}
      <main className="flex-1 overflow-y-auto bg-white">
        {!activeId && (
          <div className="h-full flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-100 to-violet-100 flex items-center justify-center mb-4">
              <BookOpen size={28} className="text-blue-600" weight="duotone" />
            </div>
            <h3 className="font-['Outfit'] text-[20px] font-bold text-zinc-800 mb-1">Project documentation</h3>
            <p className="text-[13px] text-zinc-500 max-w-md mb-5">
              Capture meeting notes, PRDs, retrospectives, decision logs and more —
              all alongside your tasks and sprints.
            </p>
            {canEdit && pages.length === 0 && (
              <Button onClick={() => handleCreate('blank')} disabled={limitReached} data-testid="page-hero-create-btn">
                <Plus size={14} className="mr-1.5" /> Create your first page
              </Button>
            )}
          </div>
        )}
        {activePage && (
          <div className="w-full px-12 py-8" data-testid="page-editor">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                {activePage.updater && (
                  <>
                    <Avatar className="h-5 w-5">
                      {activePage.updater.avatar && <AvatarImage src={activePage.updater.avatar} />}
                      <AvatarFallback className={`${getAvatarStyle(activePage.updater.user_id || activePage.updater.name).bg} text-white text-[8px] font-bold`}>{getInitials(activePage.updater.name)}</AvatarFallback>
                    </Avatar>
                    <span>Edited by <strong className="text-zinc-700">{activePage.updater.name}</strong> · {fmtTimeAgo(activePage.updated_at)}</span>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span data-testid="page-save-status" className={`text-[10px] font-medium ${
                  savingState === 'saving' ? 'text-zinc-400' :
                  savingState === 'saved' ? 'text-emerald-600' :
                  savingState === 'error' ? 'text-red-600' : 'text-zinc-400'
                }`}>
                  {savingState === 'saving' && <span className="inline-flex items-center gap-1"><ArrowsClockwise size={10} className="animate-spin" /> Saving…</span>}
                  {savingState === 'saved' && '✓ Saved'}
                  {savingState === 'error' && 'Save failed'}
                  {savingState === 'idle' && fmtTimeAgo(activePage.updated_at) ? `Last saved ${fmtTimeAgo(activePage.updated_at)}` : ''}
                </span>
              </div>
            </div>
            <input
              type="text"
              value={draftTitle}
              onChange={(e) => canEdit && setDraftTitle(e.target.value)}
              placeholder="Untitled"
              readOnly={!canEdit}
              className="w-full font-['Outfit'] text-[34px] font-black tracking-tight text-zinc-900 bg-transparent border-0 outline-none placeholder:text-zinc-300 mb-4"
              data-testid="page-title-input"
            />
            <RichEditor
              value={draftContent}
              onChange={(html) => canEdit && setDraftContent(html)}
              placeholder="Start writing or press / for templates… @ mentions teammates, paste a screenshot to attach an image."
              minHeight={400}
              mentionItems={memberMentions}
              uploadImage={uploadImage}
            />
          </div>
        )}
      </main>
    </div>
  );
}
